import { Skeleton } from "../ui/skeleton";

/**
 * What the router paints while a route is still resolving.
 *
 * Every guarded surface answers two questions over the network before it can
 * render anything — is there a session, and what may it see — and both run in
 * `beforeLoad`, ahead of the component. With nothing wired into the router's
 * pending slot those requests were spent on an empty document: on a cold load
 * the app painted the bare `<body>` background, which reads as the screen
 * having gone blank.
 *
 * These stand in for that gap. Neither guesses at the layout that is coming: a
 * wrong guess is a jump when the real page lands, which reads worse than a
 * calm, neutral wait.
 */

/**
 * The default, used for routes with no shell of their own (the auth screens,
 * the token pages). Deliberately sparse: the next screen is not known here, so
 * anything more specific would be a shape that doesn't arrive.
 */
export function RoutePending() {
  return (
    <output
      aria-label="Loading"
      className="flex min-h-svh w-full flex-col items-center justify-center gap-3 p-12"
    >
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-4 w-40" />
    </output>
  );
}

/**
 * The pending slot for a guarded shell (the HR workspace, the portal, the admin
 * dashboard).
 *
 * On a hard load this never shows: the boot splash in `index.html` covers the
 * page until the first route resolves. It is what a client-side hop into the
 * shell sees — straight after signing in, say — and it draws the same splash,
 * so both ways in look alike.
 *
 * It used to paint an outline of the shell. That outline could only guess the
 * layout (an expanded sidebar over a collapsed one, a dashboard's cards over a
 * table), so the screen jumped when the real page replaced it — and the guard
 * may yet send the visitor to `/sign-in`, where a shell outline is plain wrong.
 * A neutral splash is right for every outcome.
 */
export function GuardedShellPending() {
  return <AppSplash />;
}

/**
 * The boot splash from `index.html`, as a component. Its styles live in that
 * file (they have to paint before the CSS bundle loads), so this only repeats
 * the markup — keep the two in step.
 */
export function AppSplash() {
  return (
    <div className="boot-splash" role="status" aria-label="Loading HR Docket">
      <div className="boot-splash__body">
        <div className="boot-splash__mark">
          <img src="/logo.webp" alt="" />
        </div>
        <p className="boot-splash__name">HR Docket</p>
        <div className="boot-splash__track">
          <div className="boot-splash__bar" />
        </div>
      </div>
    </div>
  );
}
